const FEATURES = [
  {
    icon: "bi-sliders",
    iconClass: "bg-[#E6F7F3] text-[#0B8F7A]",
    title: "Category controls",
    text: "Necessary, Analytics, Advertising and Functional — toggled by the Data Principal.",
  },
  {
    icon: "bi-window-sidebar",
    iconClass: "bg-[#E8F0FE] text-[#2B6CB0]",
    title: "Banner & preference centre",
    text: "First-layer banner plus a detailed centre to review and change choices anytime.",
  },
  {
    icon: "bi-link-45deg",
    iconClass: "bg-[#FEF3C7] text-[#B45309]",
    title: "Script blocking",
    text: "Block non-essential tags until consent — keep marketing stacks compliant by design.",
  },
  {
    icon: "bi-journal-check",
    iconClass: "bg-[#F0E8FF] text-[#6B46C1]",
    title: "Proof & DSAR ready",
    text: "Timestamped consent records feed Consent Variance and Data Principal access requests.",
  },
];

export default function CookieConsent() {
  return (
    <section id="cookies" className="py-20 bg-white border-b border-line">
      <div className="container mx-auto max-w-[1140px] px-4">
        <p className="inline-block text-[0.78rem] font-semibold tracking-[0.12em] uppercase text-teal mb-[0.85rem]">
          Cookie consent
        </p>
        <h2 className="font-bricolage text-[clamp(1.75rem,3vw,2.3rem)] mb-[0.7rem] text-ink">
          Cookie banners that respect choice — and prove it
        </h2>
        <p className="text-[1.05rem] leading-[1.65] text-slate max-w-[40rem]">
          Deploy purpose-based cookie consent across websites and apps.
          Category controls, preference centre and audit-ready logs tie
          digital tracking back to the same Consent Hub as Email and
          WhatsApp.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-10 items-center mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.9rem]">
            {FEATURES.map((f) => (
              <div key={f.title} className="p-[1.1rem_1.15rem] rounded-[10px] bg-surface border border-line">
                <div className={`w-[38px] h-[38px] rounded-[9px] flex items-center justify-center text-[1.05rem] mb-[0.7rem] ${f.iconClass}`}>
                  <i className={`bi ${f.icon}`} />
                </div>
                <h4 className="text-[0.95rem] font-bricolage font-semibold mb-[0.3rem]">{f.title}</h4>
                <p className="m-0 text-[0.84rem] text-slate leading-[1.45]">{f.text}</p>
              </div>
            ))}
          </div>

          {/* browser + banner mockup */}
          <div
            className="relative rounded-2xl p-6 text-white overflow-hidden bg-ink2 shadow-[0_24px_50px_rgba(7,17,31,0.25)]"
            aria-label="Cookie consent banner preview"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 85% 15%, rgba(18,184,154,0.22), transparent 45%)" }}
            />
            <div className="relative bg-white/[.04] border border-white/10 rounded-[10px] overflow-hidden">
              <div className="flex items-center gap-[0.4rem] px-[0.85rem] py-[0.65rem] bg-black/25 border-b border-white/[.08]">
                <span className="w-2 h-2 rounded-full bg-[#F87171]" />
                <span className="w-2 h-2 rounded-full bg-[#FBBF24]" />
                <span className="w-2 h-2 rounded-full bg-[#34D399]" />
                <span className="ml-2 flex-1 text-[0.72rem] text-white/40 bg-white/[.06] px-[0.55rem] py-1 rounded">
                  https://yourbrand.in
                </span>
              </div>
              <div
                className="h-[120px]"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.03), transparent), repeating-linear-gradient(0deg, transparent, transparent 18px, rgba(255,255,255,0.03) 18px, rgba(255,255,255,0.03) 19px)",
                }}
              />
            </div>

            <div className="relative mt-[0.85rem] p-[1.15rem_1.2rem] rounded-[10px] bg-white/[.06] border border-teal-bright/[.35]">
              <div className="text-[0.65rem] tracking-[0.1em] uppercase text-teal-bright mb-[0.4rem]">
                Cookie consent · OneDPDP
              </div>
              <h4 className="text-[1rem] font-bricolage font-semibold mb-[0.35rem]">We value your privacy</h4>
              <p className="text-[0.82rem] text-white/[.55] leading-[1.45] mb-[0.9rem]">
                We use cookies to run this site and, with your consent, to
                measure performance and personalise content under the DPDP
                Act.
              </p>
              <div className="flex flex-wrap gap-[0.45rem] mb-[0.95rem]">
                <span className="inline-flex items-center gap-[0.3rem] text-[0.7rem] font-semibold px-[0.55rem] py-[0.28rem] rounded bg-white/[.06] border border-white/10 text-white/75">
                  <i className="bi bi-toggle-on text-teal-bright" /> Necessary
                </span>
                <span className="inline-flex items-center gap-[0.3rem] text-[0.7rem] font-semibold px-[0.55rem] py-[0.28rem] rounded bg-white/[.06] border border-white/10 text-white/75">
                  <i className="bi bi-toggle-on text-teal-bright" /> Analytics
                </span>
                <span className="inline-flex items-center gap-[0.3rem] text-[0.7rem] font-semibold px-[0.55rem] py-[0.28rem] rounded bg-white/[.06] border border-white/10 text-white/75">
                  <i className="bi bi-toggle-off text-white/35" /> Advertising
                </span>
                <span className="inline-flex items-center gap-[0.3rem] text-[0.7rem] font-semibold px-[0.55rem] py-[0.28rem] rounded bg-white/[.06] border border-white/10 text-white/75">
                  <i className="bi bi-toggle-off text-white/35" /> Functional
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className="text-[0.78rem] font-semibold px-[0.85rem] py-[0.45rem] rounded bg-teal text-white">
                  Accept selected
                </button>
                <button type="button" className="text-[0.78rem] font-semibold px-[0.85rem] py-[0.45rem] rounded border border-white/25 text-white/80">
                  Reject non-essential
                </button>
                <button type="button" className="text-[0.78rem] font-semibold px-[0.85rem] py-[0.45rem] rounded border border-white/25 text-white/80">
                  Manage preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
